import React from 'react';

import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Descriptions, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useFulfillmentSourceTypeLabel, useHolderTypeLabel } from '@constants/inventoryConflict';
import { useInventoryHoldReasonLabel } from '@constants/inventoryHold';
import type {
  BlockingReservation,
  InventoryConflict,
} from '@services/inventoryConflictSchemas';

const { Text } = Typography;

const ConflictCard = styled(Card)`
  margin-bottom: 16px;
  border: 1px solid #faad14;

  .ant-card-head {
    background: #fffbe6;
  }
`;

const ConflictHeaderRow = styled.div`
  margin-bottom: 12px;
`;

const RefreshRow = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
`;

const SectionAlert = styled(Alert)`
  margin-bottom: 16px;
`;

interface InventoryConflictSectionProps {
  conflicts: InventoryConflict[];
  loading: boolean;
  onRefresh: () => void;
}

const InventoryConflictSection: React.FC<InventoryConflictSectionProps> = ({
  conflicts,
  loading,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const holderTypeLabel = useHolderTypeLabel();
  const sourceTypeLabel = useFulfillmentSourceTypeLabel();
  const holdReasonLabel = useInventoryHoldReasonLabel();

  if (conflicts.length === 0) {
    return null;
  }

  const columns: ColumnsType<BlockingReservation> = [
    {
      title: t('shared.inventory_conflict.holder_label'),
      key: 'holder',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Tag color={r.holder_type === 'fulfillment' ? 'blue' : 'orange'}>
            {holderTypeLabel(r.holder_type)}
          </Tag>
          <Link
            to={
              r.holder_type === 'fulfillment'
                ? `/fulfillments/${r.holder_id}`
                : `/inventory_holds/${r.holder_id}`
            }
          >
            <strong>{r.holder_number}</strong>
          </Link>
        </Space>
      ),
    },
    {
      title: t('shared.inventory_conflict.details'),
      key: 'details',
      render: (_, r) => {
        if (r.holder_type === 'fulfillment') {
          return (
            <Space direction="vertical" size={0}>
              <Tag>{sourceTypeLabel(r.source_type)}</Tag>
              {r.order_name ? <Text>{r.order_name}</Text> : null}
              <Text type="secondary">{r.holder_status}</Text>
            </Space>
          );
        }
        return (
          <Space direction="vertical" size={0}>
            <Tag color="warning">{holdReasonLabel(r.reason ?? '')}</Tag>
            {r.held_by_name ? (
              <Text type="secondary">
                {t('shared.inventory_conflict.held_by', { name: r.held_by_name })}
              </Text>
            ) : null}
            {r.note ? <Text type="secondary">{r.note}</Text> : null}
          </Space>
        );
      },
    },
    {
      title: t('shared.inventory_conflict.reserved_quantity'),
      key: 'reserved_quantity',
      width: 100,
      align: 'right',
      render: (_, r) => <Text strong>{r.reserved_quantity}</Text>,
    },
  ];

  return (
    <div data-testid="inventory-conflict-section">
      <SectionAlert
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        message={t('shared.inventory_conflict.section_title')}
        description={t('shared.inventory_conflict.section_banner', { count: conflicts.length })}
      />

      {conflicts.map((conflict) => (
        <ConflictCard key={conflict.inventory_id} size="small">
          <ConflictHeaderRow>
            <Descriptions size="small" column={4} bordered>
              <Descriptions.Item label={t('shared.inventory_conflict.product')}>
                <Text strong>{conflict.product_sku}</Text>
                <Text type="secondary"> {conflict.product_name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.inventory_conflict.total_quantity')}>
                {conflict.total_quantity}
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.inventory_conflict.committed')}>
                <Text type="danger">
                  {t('shared.inventory_conflict.committed_detail', {
                    allocated: conflict.allocated_quantity,
                    held: conflict.held_quantity,
                  })}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.inventory_conflict.shortage')}>
                <Tag color="error">
                  {t('shared.inventory_conflict.shortage_value', { count: conflict.shortage })}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </ConflictHeaderRow>

          <Table
            columns={columns}
            dataSource={conflict.blocking_reservations}
            rowKey={(r) => `${r.holder_type}:${r.holder_id}`}
            pagination={false}
            size="small"
          />
        </ConflictCard>
      ))}

      <RefreshRow>
        <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading}>
          {t('shared.inventory_conflict.refresh_button')}
        </Button>
      </RefreshRow>
    </div>
  );
};

export default InventoryConflictSection;
